import { WebRTCManager } from './webrtc/WebRTCManager.js';
import { UIManager } from './ui/UIManager.js';
import { SignalingClient } from './signaling/SignalingClient.js';
import { Utils } from './utils/Utils.js';
import { globalErrorHandler } from './utils/ErrorHandler.js';
import { AIDevIntegration } from './services/AIDevIntegration.js';

class P2PVideoChat {
    constructor() {
        this.webrtc = null;
        this.ui = null;
        this.signaling = null;
        this.aiDev = new AIDevIntegration(); // Optional AI dev server integration
        this.currentRoomName = null;
        this.currentRoomId = null; // Keep for backward compatibility
        this.isHost = false;
        this.mediaType = 'video'; // 'video' or 'audio'
    }

    async init() {
        try {
            // Check WebRTC support first
            if (!Utils.isWebRTCSupported()) {
                this.ui.showError('WebRTC is not supported in your browser. Please use a modern browser like Chrome, Firefox, or Safari.');
                return;
            }

            // Initialize UI Manager
            this.ui = new UIManager();
            await this.ui.init();

            // Try to connect to AI dev server (optional)
            await this.aiDev.connect();

            // Set up event listeners
            this.setupEventListeners();

            // Check if joining from URL
            const urlParams = new URLSearchParams(window.location.search);
            const roomParam = urlParams.get('room');

            if (roomParam) {
                await this.joinRoom(roomParam);
            } else {
                // Auto-create a room and show camera immediately
                await this.createRoom('video');
            }

        } catch (error) {
            Utils.error('Failed to initialize app:', error);
            globalErrorHandler.handleError(error, 'app-init');
            this.ui.showError('Failed to initialize the application. Please refresh and try again.');
        }
    }

    setupEventListeners() {
        // Home screen events
        document.getElementById('create-room-btn').addEventListener('click', () => {
            this.createRoom('video');
        });

        document.getElementById('create-audio-btn').addEventListener('click', () => {
            this.createRoom('audio');
        });

        document.getElementById('join-room-btn').addEventListener('click', () => {
            const input = document.getElementById('room-id-input');
            const roomInput = input.value.trim();
            if (!roomInput) {
                this.ui.showToast('Please enter a room name or code', 'error');
                return;
            }

            // Validate input format before attempting to join
            const inputType = Utils.getRoomInputType(roomInput);
            if (!inputType) {
                this.ui.showToast('Invalid room name or code format', 'error');
                return;
            }

            this.joinRoom(roomInput);
        });

        document.getElementById('room-id-input').addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                document.getElementById('join-room-btn').click();
            }
        });

        // Room screen events
        document.getElementById('share-room-btn').addEventListener('click', () => {
            this.shareRoom();
        });

        document.getElementById('toggle-video-btn').addEventListener('click', () => {
            this.toggleVideo();
        });

        document.getElementById('toggle-audio-btn').addEventListener('click', () => {
            this.toggleAudio();
        });

        document.getElementById('end-call-btn').addEventListener('click', () => {
            this.endCall();
        });

        // Error screen events
        document.getElementById('retry-btn').addEventListener('click', () => {
            this.retryApplication();
        });

        // Listen for application retry events from error handler
        window.addEventListener('application-retry', () => {
            this.retryApplication();
        });

        // Handle page visibility changes
        document.addEventListener('visibilitychange', () => {
            if (document.hidden && this.webrtc) {
                // Optionally pause video when tab is hidden
            }
        });

        // Handle beforeunload
        window.addEventListener('beforeunload', () => {
            this.cleanup();
        });

        // Handle mobile-specific events
        if (Utils.isMobile()) {
            // Prevent zoom on double tap
            let lastTouchEnd = 0;
            document.addEventListener('touchend', (event) => {
                const now = (new Date()).getTime();
                if (now - lastTouchEnd <= 300) {
                    event.preventDefault();
                }
                lastTouchEnd = now;
            }, false);

            // Handle orientation changes
            window.addEventListener('orientationchange', () => {
                setTimeout(() => {
                    this.handleOrientationChange();
                }, 100);
            });
        }
    }

    async createRoom(mediaType = 'video') {
        try {
            Utils.debug('createRoom called with mediaType:', mediaType);
            this.ui.showScreen('loading');
            this.mediaType = mediaType;
            this.isHost = true;
            Utils.debug('mediaType set to:', this.mediaType);

            // Generate room name and ID
            this.currentRoomName = Utils.generateRoomName();
            this.currentRoomId = Utils.generateRoomId(); // Keep for backward compatibility

            // Initialize signaling
            this.signaling = new SignalingClient();
            await this.signaling.connect();

            // Initialize WebRTC
            this.webrtc = new WebRTCManager(this.signaling);
            await this.webrtc.init(mediaType === 'video');

            // Set up WebRTC event listeners
            this.setupWebRTCEventListeners();

            // Create room on signaling server
            await this.signaling.createRoom(this.currentRoomId, this.currentRoomName);

            // Update UI
            Utils.debug('Updating room info with:', { roomName: this.currentRoomName, roomId: this.currentRoomId });
            this.ui.updateRoomInfo(this.currentRoomName, this.currentRoomId);
            this.ui.showScreen('room');

            // Set audio-only mode if needed
            if (mediaType === 'audio') {
                Utils.debug('Setting audio-only mode to true');
                this.ui.setAudioOnlyMode(true);
            }

            this.ui.updateConnectionStatus('ready', 'Ready to connect');

            // Update URL with room name
            const newUrl = `${window.location.origin}${window.location.pathname}?room=${encodeURIComponent(this.currentRoomName)}`;
            window.history.pushState({ roomName: this.currentRoomName, roomId: this.currentRoomId }, '', newUrl);

            this.ui.showToast('Room created! Share the link to invite someone.', 'success');

        } catch (error) {
            Utils.error('Failed to create room:', error);
            globalErrorHandler.handleError(error, 'create-room');
            const errorMessage = Utils.formatError(error);
            this.ui.showError(`Failed to create room: ${errorMessage}`);
        }
    }

    async joinRoom(roomInput) {
        try {
            this.ui.showScreen('loading');

            // Input is already validated in the event listener
            const inputType = Utils.getRoomInputType(roomInput);

            // Extract room name from input (could be URL, room name, or room ID)
            if (inputType === 'name') {
                this.currentRoomName = roomInput;
                this.currentRoomId = roomInput; // For now, use the name as ID
            } else if (inputType === 'id') {
                this.currentRoomName = `Room ${roomInput}`;
                this.currentRoomId = roomInput;
            } else {
                // Handle URL or other formats
                const extractedRoom = this.extractRoomId(roomInput);
                this.currentRoomName = extractedRoom;
                this.currentRoomId = extractedRoom; // For now, use extracted value as ID
            }

            Utils.debug('Joining room with:', { roomInput, inputType, currentRoomName: this.currentRoomName, currentRoomId: this.currentRoomId });

            this.isHost = false;

            // Initialize signaling
            this.signaling = new SignalingClient();
            await this.signaling.connect();

            // Initialize WebRTC (assume video for now, will be adjusted based on host)
            this.webrtc = new WebRTCManager(this.signaling);
            await this.webrtc.init(true);

            // Set up WebRTC event listeners
            this.setupWebRTCEventListeners();

            // Join room on signaling server
            await this.signaling.joinRoom(roomInput);

            // Update UI
            this.ui.updateRoomInfo(this.currentRoomName, this.currentRoomId);
            this.ui.showScreen('room');
            this.ui.updateConnectionStatus('connecting', 'Connecting...');

            // Update URL
            const newUrl = `${window.location.origin}${window.location.pathname}?room=${encodeURIComponent(this.currentRoomName)}`;
            window.history.pushState({ roomName: this.currentRoomName, roomId: this.currentRoomId }, '', newUrl);

        } catch (error) {
            Utils.error('Failed to join room:', error);
            Utils.debug('Error stack:', error.stack);
            globalErrorHandler.handleError(error, 'join-room');
            const errorMessage = Utils.formatError(error);
            this.ui.showError(`Failed to join room: ${errorMessage}`);
        }
    }

    setupWebRTCEventListeners() {
        this.webrtc.addEventListener('localStream', (event) => {
            const stream = event.detail;
            this.ui.setLocalVideo(stream);

            // Check if this is an audio-only stream and update UI accordingly
            const hasVideoTrack = stream.getVideoTracks().length > 0;
            if (!hasVideoTrack && this.mediaType === 'audio') {
                this.ui.setAudioOnlyMode(true);
            }
        });

        this.webrtc.addEventListener('remoteStream', (event) => {
            const stream = event.detail;
            this.ui.setRemoteVideo(stream);
            this.ui.updateConnectionStatus('connected', 'Connected');
        });

        this.webrtc.addEventListener('connectionStateChange', (event) => {
            const state = event.detail;
            Utils.debug('Connection state:', state);
            
            // Report to AI dev server
            this.aiDev.reportWebRTCStatus(state);
            
            switch (state) {
            case 'connected':
                this.ui.updateConnectionStatus('connected', 'Connected');
                break;
            case 'disconnected':
            case 'failed':
                this.ui.updateConnectionStatus('disconnected', 'Connection lost');
                break;
            case 'connecting':
                this.ui.updateConnectionStatus('connecting', 'Connecting...');
                break;
            }
        });

        this.webrtc.addEventListener('error', (event) => {
            const error = event.detail;
            Utils.error('WebRTC error:', error);
            const errorMessage = Utils.formatError(error);
            this.ui.showToast(`Connection error: ${errorMessage}`, 'error');
            
            // Report error to AI dev server
            this.aiDev.reportError(error, { 
                type: 'webrtc_error',
                roomId: this.currentRoomId,
                roomName: this.currentRoomName
            });
        });

        this.webrtc.addEventListener('peerDisconnected', () => {
            this.ui.showToast('Peer disconnected', 'error');
            this.ui.updateConnectionStatus('disconnected', 'Peer left');
        });

        this.webrtc.addEventListener('videoUnavailable', () => {
            this.ui.showToast('Video unavailable, continuing with audio only', 'warning');
            this.ui.updateVideoButton(false);
        });

        // Set up signaling event listeners
        this.signaling.addEventListener('peerJoined', async (event) => {
            Utils.debug('Peer joined, creating offer...');
            if (this.isHost) {
                await this.webrtc.createOffer();
            }
        });

        this.signaling.addEventListener('offer', async (event) => {
            Utils.debug('Received offer, creating answer...');
            const offer = event.detail;
            await this.webrtc.handleOffer(offer);
        });

        this.signaling.addEventListener('answer', async (event) => {
            Utils.debug('Received answer...');
            const answer = event.detail;
            await this.webrtc.handleAnswer(answer);
        });

        this.signaling.addEventListener('iceCandidate', async (event) => {
            Utils.debug('Received ICE candidate...');
            const candidate = event.detail;
            await this.webrtc.handleIceCandidate(candidate);
        });
    }

    async shareRoom() {
        if (!this.currentRoomName) return;

        const roomUrl = `${window.location.origin}${window.location.pathname}?room=${encodeURIComponent(this.currentRoomName)}`;

        try {
            if (navigator.share && Utils.isMobile()) {
                // Use native sharing if available (mobile)
                await navigator.share({
                    title: 'Join my video call',
                    text: `Join my ${this.mediaType} call: ${this.currentRoomName}`,
                    url: roomUrl
                });
            } else if (navigator.clipboard) {
                // Fallback to clipboard
                await navigator.clipboard.writeText(roomUrl);
                this.ui.showToast('Room link copied to clipboard!', 'success');
            } else {
                // Fallback: show the URL in a prompt
                prompt('Copy this link to share:', roomUrl);
            }
        } catch (error) {
            Utils.error('Failed to share:', error);
            // Final fallback: show the URL in a prompt
            prompt('Copy this link to share:', roomUrl);
        }
    }

    toggleVideo() {
        if (!this.webrtc) return;

        const isEnabled = this.webrtc.toggleVideo();
        this.ui.updateVideoButton(isEnabled);

        if (isEnabled) {
            this.ui.showToast('Camera on', 'success');
        } else {
            this.ui.showToast('Camera off', 'error');
        }
    }

    toggleAudio() {
        if (!this.webrtc) return;

        const isEnabled = this.webrtc.toggleAudio();
        this.ui.updateAudioButton(isEnabled);

        if (isEnabled) {
            this.ui.showToast('Microphone on', 'success');
        } else {
            this.ui.showToast('Microphone off', 'error');
        }
    }

    endCall() {
        this.cleanup();

        // Clear URL
        window.history.pushState({}, '', window.location.pathname);

        // Return to home screen
        this.ui.showScreen('home');
        this.ui.showToast('Call ended', 'success');

        // Reset state
        this.currentRoomName = null;
        this.currentRoomId = null;
        this.isHost = false;
    }

    cleanup() {
        if (this.webrtc) {
            this.webrtc.cleanup();
            this.webrtc = null;
        }

        if (this.signaling) {
            this.signaling.cleanup();
            this.signaling = null;
        }

        // Clean up AI dev integration
        if (this.aiDev) {
            this.aiDev.cleanup();
        }

        // Clear any pending timeouts
        if (this.cleanupTimeout) {
            clearTimeout(this.cleanupTimeout);
            this.cleanupTimeout = null;
        }
    }

    handleOrientationChange() {
        // Handle mobile orientation changes
        if (Utils.isMobile()) {
            // Force video elements to resize properly
            const localVideo = document.getElementById('local-video');
            const remoteVideo = document.getElementById('remote-video');

            if (localVideo && localVideo.srcObject) {
                localVideo.style.width = 'auto';
                localVideo.style.height = 'auto';
            }

            if (remoteVideo && remoteVideo.srcObject) {
                remoteVideo.style.width = 'auto';
                remoteVideo.style.height = 'auto';
            }
        }
    }

    extractRoomId(input) {
        // Handle full URLs
        if (input.includes('room=')) {
            const url = new URL(input.includes('://') ? input : `https://${input}`);
            return url.searchParams.get('room');
        }

        // Handle direct room names or codes
        return input.trim();
    }

    /**
     * Retry the application after an error
     */
    retryApplication() {
        try {
            // Clean up current state
            this.cleanup();

            // Reset state
            this.currentRoomName = null;
            this.currentRoomId = null;
            this.isHost = false;
            this.mediaType = 'video';

            // Show home screen
            this.ui.showScreen('home');

            // Show success message
            this.ui.showToast('Application reset successfully. You can try again.', 'success');

        } catch (error) {
            Utils.error('Failed to retry application:', error);
            globalErrorHandler.handleError(error, 'retry-application');
            // If retry fails, reload the page as last resort
            window.location.reload();
        }
    }
}

// Initialize app when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    const app = new P2PVideoChat();
    window.p2pVideoChat = app; // Expose for debugging
    app.init().catch(error => Utils.error('App initialization failed:', error));
});

// Handle browser back/forward
window.addEventListener('popstate', (event) => {
    if (event.state && (event.state.roomName || event.state.roomId)) {
        // Handle navigation back to room
        window.location.reload();
    } else {
        // Handle navigation back to home
        window.location.reload();
    }
});
